/**
 * Compliance Controls — v32 customer-gap fill
 * ============================================
 * Two-tab admin surface for the customer-gap features:
 *   1) Sovereignty locks — mark resources as compliance-required so
 *      region-migration recommendations are suppressed
 *   2) PII-aware proxy policy — view + edit which providers can receive
 *      PII payloads + browse the routing log
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ShieldCheck, ShieldOff, Globe2, Lock, Plus, RefreshCcw, ArrowLeft, FileLock2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { id: 'gdpr_residency', label: 'GDPR (EU data residency)' },
  { id: 'uae_data_law',   label: 'UAE data law' },
  { id: 'india_dpdp',     label: 'India DPDP' },
  { id: 'us_state_law',   label: 'US state law (CCPA/CPRA)' },
  { id: 'sox',            label: 'SOX' },
  { id: 'hipaa',          label: 'HIPAA' },
  { id: 'pci_dss',        label: 'PCI DSS' },
  { id: 'other',          label: 'Other (note required)' },
];

const ComplianceControls = () => {
  const [tab, setTab] = useState('locks');

  // Locks state
  const [locks, setLocks] = useState([]);
  const [newLock, setNewLock] = useState({ resource_id: '', region: '', reason: 'gdpr_residency', note: '' });
  const [locksLoading, setLocksLoading] = useState(true);

  // PII policy state
  const [policy, setPolicy] = useState(null);
  const [piiLog, setPiiLog] = useState([]);
  const [policyDirty, setPolicyDirty] = useState(false);

  const loadLocks = async () => {
    setLocksLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/compliance-locks`);
      setLocks(data?.locks || []);
    } catch (e) { toast.error('Could not load compliance locks'); }
    finally { setLocksLoading(false); }
  };

  const loadPolicy = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/pii-router/policy`);
      setPolicy(data);
      setPolicyDirty(false);
    } catch (e) { toast.error('Could not load PII policy'); }
  };

  const loadPiiLog = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/pii-router/log`);
      setPiiLog(data?.entries || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    document.title = 'Compliance Controls · CARTIEAI';
    loadLocks();
    loadPolicy();
    loadPiiLog();
  }, []);

  const handleLock = async () => {
    if (!newLock.resource_id.trim() || !newLock.reason) {
      toast.error('resource_id and reason are required');
      return;
    }
    try {
      await axios.post(`${API_URL}/compliance-locks/lock`, newLock);
      toast.success('Lock saved', { description: `${newLock.resource_id} is now compliance-locked` });
      setNewLock({ resource_id: '', region: '', reason: 'gdpr_residency', note: '' });
      loadLocks();
    } catch (e) {
      toast.error('Lock failed', { description: e?.response?.data?.detail || e?.message });
    }
  };

  const handleUnlock = async (rid) => {
    try {
      await axios.post(`${API_URL}/compliance-locks/unlock`, { resource_id: rid });
      toast.success('Lock removed');
      loadLocks();
    } catch (e) {
      toast.error('Unlock failed', { description: e?.response?.data?.detail || e?.message });
    }
  };

  const handleSavePolicy = async () => {
    try {
      await axios.put(`${API_URL}/pii-router/policy`, policy);
      toast.success('PII policy saved');
      setPolicyDirty(false);
      loadPiiLog();
    } catch (e) {
      toast.error('Save failed', { description: e?.response?.data?.detail || e?.message });
    }
  };

  const updPolicy = (patch) => {
    setPolicy({ ...policy, ...patch });
    setPolicyDirty(true);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Link to="/admin/leads" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-700 mb-2" data-testid="cc-back-link">
              <ArrowLeft className="w-3 h-3" /> Back to admin
            </Link>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" data-testid="cc-h1">
              <ShieldCheck className="w-7 h-7 inline mr-2 text-emerald-600" />
              Compliance Controls
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-3xl">
              Two enterprise-aware controls: <strong>Sovereignty locks</strong> stop our recommendation engine from suggesting region migrations on data-residency-bound resources. The <strong>PII-aware proxy policy</strong> automatically reroutes LLM requests containing PII to sovereign providers.
            </p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} data-testid="cc-tabs">
          <TabsList>
            <TabsTrigger value="locks" data-testid="tab-locks"><FileLock2 className="w-4 h-4 mr-1.5" /> Sovereignty locks</TabsTrigger>
            <TabsTrigger value="pii" data-testid="tab-pii"><Globe2 className="w-4 h-4 mr-1.5" /> PII-aware proxy</TabsTrigger>
          </TabsList>

          {/* ───────────────── LOCKS ───────────────── */}
          <TabsContent value="locks" className="space-y-4 mt-4">
            <Card className="border-emerald-200">
              <CardHeader>
                <CardTitle className="text-lg">Lock a resource as compliance-required</CardTitle>
                <CardDescription className="text-slate-400">
                  Region-migration recommendations on locked resources are silently suppressed. Other rec types (resize, commitment) still surface.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="lock-form">
                  <Input
                    placeholder="Resource ID (e.g. i-0a8b2c4)"
                    value={newLock.resource_id}
                    onChange={(e) => setNewLock({ ...newLock, resource_id: e.target.value })}
                    data-testid="lock-resource-id"
                  />
                  <Input
                    placeholder="Region (e.g. eu-west-1)"
                    value={newLock.region}
                    onChange={(e) => setNewLock({ ...newLock, region: e.target.value })}
                    data-testid="lock-region"
                  />
                  <select
                    value={newLock.reason}
                    onChange={(e) => setNewLock({ ...newLock, reason: e.target.value })}
                    className="bg-white border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900"
                    data-testid="lock-reason"
                  >
                    {REASONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                  <Button onClick={handleLock} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold" data-testid="lock-submit">
                    <Plus className="w-4 h-4 mr-1" /> Add lock
                  </Button>
                </div>
                <Input
                  className="mt-2"
                  placeholder="Note (optional — shown in audit log)"
                  value={newLock.note}
                  onChange={(e) => setNewLock({ ...newLock, note: e.target.value })}
                  data-testid="lock-note"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Active locks ({locks.length})</CardTitle>
                  <CardDescription className="text-slate-400">Every active lock suppresses region-migration recommendations.</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={loadLocks} data-testid="locks-refresh">
                  <RefreshCcw className="w-3 h-3 mr-1" /> Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {locksLoading ? (
                  <p className="text-sm text-slate-500">Loading…</p>
                ) : locks.length === 0 ? (
                  <p className="text-sm text-slate-500" data-testid="locks-empty">No active compliance locks. Add one above.</p>
                ) : (
                  <div className="space-y-2" data-testid="locks-list">
                    {locks.map((l) => (
                      <div key={l.resource_id} className="flex items-center justify-between gap-3 p-3 rounded-md border border-emerald-200 bg-emerald-50/40" data-testid={`lock-row-${l.resource_id}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-emerald-700" />
                            <code className="text-sm font-semibold text-slate-900">{l.resource_id}</code>
                            {l.region && <Badge variant="outline" className="text-[10px]">{l.region}</Badge>}
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">
                              {REASONS.find((r) => r.id === l.reason)?.label || l.reason}
                            </Badge>
                          </div>
                          {l.note && <div className="text-xs text-slate-600 mt-1 truncate">{l.note}</div>}
                          <div className="text-[10px] text-slate-500 mt-1 font-mono">
                            locked by {l.locked_by_user_email} · {new Date(l.locked_at).toLocaleString()}
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => handleUnlock(l.resource_id)} data-testid={`unlock-${l.resource_id}`}>
                          <ShieldOff className="w-3 h-3 mr-1" /> Unlock
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ───────────────── PII PROXY ───────────────── */}
          <TabsContent value="pii" className="space-y-4 mt-4">
            <Card className="border-emerald-200">
              <CardHeader>
                <CardTitle className="text-lg">PII routing policy</CardTitle>
                <CardDescription className="text-slate-400">
                  When CARTIE's proxy detects PII in an LLM prompt (email, SSN, credit card, phone, IPv4, Aadhaar, passport), this policy decides whether to allow, reroute to a sovereign provider, or refuse.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {policy ? (
                  <div className="space-y-4" data-testid="pii-policy-form">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={!!policy.enabled}
                        onChange={(e) => updPolicy({ enabled: e.target.checked })}
                        data-testid="pii-enabled"
                      />
                      <label className="text-sm font-semibold text-slate-900">Policy enabled</label>
                    </div>

                    <div>
                      <label className="text-xs text-slate-600 font-semibold block mb-1">On PII detected</label>
                      <select
                        value={policy.on_pii_detected}
                        onChange={(e) => updPolicy({ on_pii_detected: e.target.value })}
                        className="bg-white border border-slate-300 rounded-md px-3 py-2 text-sm w-full"
                        data-testid="pii-mode"
                      >
                        <option value="force_sovereign">Reroute to a sovereign provider (recommended)</option>
                        <option value="refuse">Refuse the request</option>
                        <option value="log_only">Allow but log (advisory mode)</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-xs text-slate-600 font-semibold block mb-1">Sovereign providers (comma-separated, in priority order)</label>
                      <Input
                        value={(policy.sovereign_providers || []).join(', ')}
                        onChange={(e) => updPolicy({ sovereign_providers: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                        placeholder="azure-openai-eu, bedrock-eu-central-1, vertex-europe-west4"
                        data-testid="pii-sovereign-providers"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-slate-600 font-semibold block mb-1">Global providers explicitly allowed for PII (comma-separated)</label>
                      <Input
                        value={(policy.allowed_global_providers || []).join(', ')}
                        onChange={(e) => updPolicy({ allowed_global_providers: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                        placeholder="(leave empty to deny all global providers when PII detected)"
                        data-testid="pii-allowed-globals"
                      />
                    </div>

                    <Button onClick={handleSavePolicy} disabled={!policyDirty} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold" data-testid="pii-save">
                      {policyDirty ? 'Save policy' : 'No changes'}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Loading policy…</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Recent routing decisions ({piiLog.length})</CardTitle>
                  <CardDescription className="text-slate-400">Every PII-aware routing decision is logged for SOC 2 audit.</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={loadPiiLog} data-testid="pii-log-refresh">
                  <RefreshCcw className="w-3 h-3 mr-1" /> Refresh
                </Button>
              </CardHeader>
              <CardContent>
                {piiLog.length === 0 ? (
                  <p className="text-sm text-slate-500" data-testid="pii-log-empty">No PII routing events yet.</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="pii-log-list">
                    {piiLog.map((e, i) => (
                      <div key={i} className="p-3 rounded-md border border-slate-200 bg-slate-50 text-xs font-mono">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-slate-500">{new Date(e.ts).toLocaleString()}</span>
                          <Badge className={
                            e.decision === 'rerouted' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                            e.decision === 'refuse' ? 'bg-rose-100 text-rose-700 border-rose-300' :
                            'bg-emerald-100 text-emerald-700 border-emerald-300'
                          }>{e.decision}</Badge>
                        </div>
                        <div className="mt-1 text-slate-700">
                          <span className="text-slate-500">PII:</span> {e.pii_kinds?.join(', ') || '—'} ·
                          <span className="text-slate-500"> req:</span> {e.requested_provider} →
                          <span className="text-slate-500"> use:</span> {e.use_provider || '(refused)'}
                        </div>
                        <div className="text-slate-500 mt-1">{e.reason}</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ComplianceControls;
